import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import CommentsList from "@Components/Comments/CommentsList";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import CustomFields from "@Components/IssuesCustomFields/CustomFields";
import { defaultModulesMapper, folderPermissions, formCategories, getViewerUrl } from "@Helpers";
import { getIssueViewerUrl } from "../../../utils/issueViewerNavigation";
import {
  getIssuesData,
  fetchIssueById,
  isIssueRequiredPermission,
  updateIssueCustomFieldsRequest,
} from "@Features/issues/state/bcfData.actions";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import { useNavigate } from "react-router-dom";
import IssueStatusPopover from "../../IssueStatusPopover/IssueStatusPopover";
import { GrClose } from "react-icons/gr";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { useFormValidator, TEMPLATE_ENTITY_SIDEBAR } from "@Features/forms";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useTranslation } from "@i18n";
import { errorCodes } from "@Helpers/errorCodes";
import { getImageUrl } from "@Utils";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";
import Text from "@CubeComponents/html/Text/Text";
import ModuleUsersAccess from "@Components/ModuleUsersAccess";
import { BiLinkExternal } from "react-icons/bi";
import type { IssueItem } from "@ServerTypes/issues/issues.schema";
import type { FormTemplateField } from "@ServerTypes/forms/forms.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { AppDispatch, RootState } from "@Reducers/index";
const { RESOURCE_COMMENT, RESOURCE_MODIFY } = folderPermissions();
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";

interface AttachedFile {
  name: string;
  fileVersion: string;
  resourceType: string;
}

/**
 * One selected row as this panel reads it. The row is assembled by whichever grid owns the
 * selection — the Issues grid, `IssuesInReview`, or a file-manager row — so every field is optional
 * and only the ones read below are named. On a transmittal-issue row `issueId`/`fileVersion` stand
 * in for `id`/`fileVersionId`.
 */
interface IssueDetailsRow {
  id?: ObjectIdString;
  issueId?: ObjectIdString;
  transmittalVersionId?: string | null;
  spaceId?: ObjectIdString;
  fileVersionId?: ObjectIdString;
  fileVersion?: ObjectIdString;
  formTemplate?: IssueItem["formTemplate"];
}

interface IssueDetailsProps {
  rowData: IssueDetailsRow[];
  allowCloseBtn?: boolean;
  closePropertiesCallback?: () => void;
  updateGridRowData?: (id: string, updates: Record<string, unknown>) => void;
}

type SideBarState = "no-records" | "multiple-records" | "display-row";

interface AccordionState {
  access: boolean;
  customFields?: boolean;
}

function IssueDetails({
  rowData: selectedRecordsData,
  allowCloseBtn = false,
  closePropertiesCallback,
  updateGridRowData,
}: IssueDetailsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [rowData, setRowData] = useState<IssueDetailsRow>({});
  const [sideBarState, setSideBarState] = useState<SideBarState>("no-records");
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    // Update rowData
    setRowData(selectedRecordsData.length === 1 ? selectedRecordsData[0]! : {});

    // Update sideBarState
    if (selectedRecordsData.length === 0) {
      setSideBarState("no-records");
    } else if (selectedRecordsData.length > 1) {
      setSideBarState("multiple-records");
    } else if (selectedRecordsData.length === 1) {
      setSideBarState("display-row");
    }
  }, [selectedRecordsData]);

  const [accordionState, setAccordionState] = useState<AccordionState>({ access: false });

  const formValidator = useFormValidator({});
  const [customFieldsState, setCustomFieldsState] = useState<{
    formTemplate: { template: FormTemplateField[] } | null;
    isEditing: boolean;
    hasMetadataFields: boolean;
    formResetKey: number;
  }>({
    formTemplate: null,
    isEditing: false,
    hasMetadataFields: false,
    formResetKey: 0,
  });

  const fileVersionId = rowData.transmittalVersionId ? rowData.fileVersion : rowData.fileVersionId;
  // Both branches are optional on the row type, but everything below only renders for a real
  // selected issue, so the id is present. One erased assertion at the source instead of one per read.
  const issueId = (rowData.transmittalVersionId ? rowData.issueId : rowData.id)!;
  const { spaceId } = rowData;

  const issues = useSelector((state: RootState) => state.issues);
  const issue = issues[issueId] as IssueItem;

  // Drop the previous issue's validation rules when the selection changes.
  //
  // `<IssueDetails>` is rendered WITHOUT a `key` (Issues.tsx), so switching grid rows swaps `rowData`
  // on the same component instance and this validator — created once at mount — survives. `updateRules`
  // only merges, and rule keys are question `_id`s, so two issues on different templates contribute
  // disjoint keys and the old ones are never overwritten. `validateAll()` walks the whole bag, so
  // issue A's mandatory metadata field keeps failing after issue B is selected, and it fails invisibly:
  // the field is not rendered, so nothing is marked. `handleUpdateCustomFields` then returns at its
  // `validateAll()` gate with no toast — Update just does nothing.
  //
  // `formResetKey` cannot cover this: it remounts FormPreview, which does not touch the rule bag.
  // Ordering is safe — FormPreview repopulates from a MOUNT-only effect (formPreview.tsx), and it only
  // remounts once `formResetKey` is bumped by the effect below, which is a state update and therefore a
  // later commit than this reset.
  const { resetRules: resetValidationRules } = formValidator;
  useEffect(() => {
    resetValidationRules();
  }, [issueId, resetValidationRules]);

  // Seed the sidebar metadata editor from the issue's embedded formTemplate. Only isMetadata fields
  // are editable/validated; the Edit button is disabled when the template has no isMetadata field.
  // `?? true` matches FormPreview's own filter: a field authored before the toggles existed carries
  // no flag and still counts as metadata.
  useEffect(() => {
    if (issue?.formTemplate) {
      const template = issue.formTemplate;
      const hasMetadataField = template.some((section) =>
        section.questionFields?.some((question) => question.isMetadata ?? true),
      );
      setCustomFieldsState((prev) => {
        // Don't clobber an active edit if the issue is refetched (redux ref changes) mid-edit.
        if (prev.isEditing) return prev;
        return {
          ...prev,
          formTemplate: { template },
          hasMetadataFields: hasMetadataField,
          isEditing: false,
          formResetKey: prev.formResetKey + 1,
        };
      });
    }
  }, [issue?.formTemplate]);

  const storedAttachedFiles = useSelector((state: RootState) => state.issues[issueId]?.attachedFiles);
  const attachedFiles = useMemo<AttachedFile[]>(
    () =>
      (storedAttachedFiles ?? []).map((file) => ({
        name: file.originalName ?? "",
        fileVersion: file.fileVersionId ?? "",
        resourceType: file.resourceType ?? "",
      })),
    [storedAttachedFiles],
  );

  const handleOpenFileLink = () => {
    // The helper takes a full `IssueItem`; a selected row carries only the four fields it reads
    // (`fileVersionId`, `id`, `spaceId`, `projectUniqueId`). Erased assertion at the boundary.
    const location = getIssueViewerUrl(rowData as typeof rowData & IssueItem);
    if (location) {
      navigate(location);
    }
  };

  const closePropertiesHandler = () => {
    if (closePropertiesCallback) {
      closePropertiesCallback();
    }
  };

  const init = useCallback(() => {
    setCustomFieldsState((prev) => ({
      formTemplate: null,
      isEditing: false,
      hasMetadataFields: false,
      formResetKey: prev.formResetKey + 1,
    }));
    if (issueId) {
      const params = { issueId };
      dispatch(getIssuesData(params));
    }
  }, [issueId]);

  // After a status change (from the status popover or the Reviews accordion) re-fetch the issue so the
  // panel reflects the authoritative status (possibly "Varies"), then refresh the grid row to match.
  const refreshIssueAndGrid = async () => {
    const fresh = await dispatch(fetchIssueById(issueId));
    if (fresh) {
      updateGridRowData?.(issueId, { statusInReview: fresh.statusInReview });
    }
  };

  useEffect(() => {
    init();
  }, [init]);

  const handleOpenLink = (dependency: AttachedFile, newTab: boolean) => {
    const location = getViewerUrl({
      fileVersionId: dependency.fileVersion,
      spaceId: issue.spaceId,
      projectId: issue.projectUniqueId,
    });
    if (location) {
      newTab ? window.open(location, "_blank") : navigate(location);
    }
  };

  const accessHeader = () => (
    <div>
      <Text>{t("Access")}</Text>
    </div>
  );

  const customFieldsHeader = () => (
    <div>
      <Text>{t("Custom Fields")}</Text>
    </div>
  );

  const accessContent = () => {
    return (
      <ModuleUsersAccess
        module={defaultModulesMapper.ISSUES}
        resourceId={issueId}
        // issue.users is `unknown[]` server-side (issues.schema users: z.array(z.unknown())); bridge to the component's shape
        users={(issue.users ?? []) as React.ComponentProps<typeof ModuleUsersAccess>["users"]}
        spaceId={issue.spaceId}
      />
    );
  };
  /**
   * Editable metadata editor (mirrors the mailbox MailSidebar): renders only isMetadata fields
   * (FormPreview filters by templateSource=TEMPLATE_ENTITY_SIDEBAR) and persists them via
   * updateIssueCustomFields.
   *
   * Answers arrive through `setFormData`, which `handleCustomFieldData` calls SYNCHRONOUSLY — not
   * through `handleData`, which it routes via a 300 ms `debouncedSave`. The debounce is there for the
   * per-field autosave (`handleSaveCustomFields`) that other surfaces use; taking the template through
   * it costs two edits. Clicking Update inside the debounce window posts the value from before the last
   * keystroke while `formValidator` — which records values synchronously — validates the new one, so a
   * stale answer saves with no error. Worse, `handleCustomFieldData` rebuilds from `formData` each time,
   * so with `formData` lagging, two edits to DIFFERENT fields inside one window clobber each other and
   * only the last survives. Both disappear once this state updates on the keystroke.
   */
  const setCustomFieldsFormData: React.ComponentProps<typeof FormPreview>["setFormData"] = (updater) => {
    setCustomFieldsState((prev) => {
      const current = prev.formTemplate ?? { template: [] };
      return { ...prev, formTemplate: { template: updater(current).template ?? [] } };
    });
  };

  const handleStartEditCustomFields = () => setCustomFieldsState((prev) => ({ ...prev, isEditing: true }));

  const handleCancelEditCustomFields = () =>
    setCustomFieldsState((prev) => ({
      ...prev,
      isEditing: false,
      formTemplate: issue?.formTemplate ? { template: issue.formTemplate } : null,
      formResetKey: prev.formResetKey + 1,
    }));

  const customFieldsErrorText = (errorCode: number | undefined) => {
    switch (errorCode) {
      case errorCodes.ISSUE_NOT_FOUND:
        return t("Issue not found.");
      case errorCodes.ISSUE_NO_ACCESS:
        return t("You do not have permission to edit this issue.");
      case errorCodes.ISSUE_CUSTOM_FIELDS_VALIDATION_FAILED:
        return t("Fill all fields according to the rules before saving.");
      default:
        return t("Failed to update Custom Fields.");
    }
  };

  const handleUpdateCustomFields = async () => {
    if (!formValidator.validateAll?.()) return;
    const template = customFieldsState.formTemplate?.template;
    if (!template) return;

    setButtonLoading(true);
    try {
      const response = await updateIssueCustomFieldsRequest({ issueId, formTemplate: template });
      if (response.data?.status) {
        dispatch(showToastNotification({ content: t("Fields updated successfully."), type: TOAST_TYPE_SUCCESS }));
        setCustomFieldsState((prev) => ({ ...prev, isEditing: false }));
        await refreshIssueAndGrid();
      } else {
        dispatch(
          showToastNotification({
            content: customFieldsErrorText(response.data?.errorCode),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch {
      dispatch(showToastNotification({ content: customFieldsErrorText(undefined), type: TOAST_TYPE_ERROR }));
    } finally {
      setButtonLoading(false);
    }
  };

  // Two independent gates on the Edit affordance. Structural: the template must actually have an
  // isMetadata question, or there is nothing the sidebar can edit. Permission: `updateIssueCustomFields`
  // is authorised server-side with RESOURCE_MODIFY (`checkIssuesPermission`), so a view-only user must
  // not be offered a form whose submit can only fail with ISSUE_NO_ACCESS.
  const canEditCustomFields =
    customFieldsState.hasMetadataFields && isIssueRequiredPermission(issue, RESOURCE_MODIFY.id);

  const customFieldsContent = () => (
    <div className="flex flex-col py-2 gap-4">
      {canEditCustomFields && (
        <div className="flex gap-2">
          {customFieldsState.isEditing ? (
            <>
              <ButtonComponent
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                onClick={handleUpdateCustomFields}
                loading={buttonLoading}
              >
                <Text>{t("Update")}</Text>
              </ButtonComponent>
              <ButtonComponent
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                onClick={handleCancelEditCustomFields}
                disabled={buttonLoading}
              >
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </>
          ) : (
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleStartEditCustomFields}>
              <div className="flex gap-2 items-center">
                <IconComponent name="edit" height="1rem" width="1rem" />
                <Text>{t("Edit")}</Text>
              </div>
            </ButtonComponent>
          )}
        </div>
      )}
      <FormPreview
        key={customFieldsState.formResetKey}
        formData={customFieldsState.formTemplate}
        disabled={!customFieldsState.isEditing}
        fullWidthInput
        resourceType={formCategories.issues}
        templateSource={TEMPLATE_ENTITY_SIDEBAR}
        setFormData={setCustomFieldsFormData}
        formValidator={formValidator}
      />
    </div>
  );

  const getSnapshotUrl = () => {
    let url = `${getImageUrl()}/Issues_default_image.svg`;
    if (issue?.snapshot) {
      url = getUrlForImage({ id: issueId }, "IssueImage");
    }
    return url;
  };

  const renderSideBar = () => (
    <div className="h-full w-160 overflow-auto text-sm">
      {issue && (
        <div className="overflow-auto border border-[#c6d8ec] p-4 border-e-8 h-full">
          <div className="grid grid-cols-[auto_auto] justify-between">
            {rowData.fileVersionId && (
              <ButtonComponent type="button" buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleOpenFileLink}>
                {t("View in Viewer")}
              </ButtonComponent>
            )}
            {allowCloseBtn ? <GrClose size={14} onClick={closePropertiesHandler} /> : undefined}
          </div>
          <div className="grid grid-cols-[auto_auto] justify-between py-5">
            <div className="font-bold">{issue.title}</div>
            <div>
              <ToolTipComponent
                showTipPointer
                position="bottom"
                content={convertDate(Number(issue.createdDate), "dateForText")}
              >
                {convertDate(Number(issue.createdDate), "dateForTooltip")}
              </ToolTipComponent>
            </div>
          </div>
          <div className="pt-3">
            <img src={getSnapshotUrl()} className="h-[11.625rem] w-[22.5rem] object-cover" alt="snapshot" />
          </div>
          <div className="pt-3">
            <SimpleEditor value={sanitizeHtml(issue.description)} editable={false} showToolbar={false} />
          </div>
          <div className="mt-8 flex flex-col gap-4 text-sm">
            <div className="flex items-center gap-8">
              <Text className="font-bold">{t("Created by")}</Text>
              <span>{issue.createdByName}</span>
            </div>
            {issue.assignedTo?.length ? (
              <div className="flex items-center gap-6">
                <Text className="font-bold">{t("Assigned To")}</Text>
                <UserNameTagComponent
                  showAvatarOnly={issue.assignedTo.length != 1}
                  userIdsForGroup={
                    issue.assignedTo.length == 1
                      ? undefined
                      : issue.assignedTo.map((u: NonNullable<IssueItem["assignedTo"]>[number]) => u._id)
                  }
                  id={issue.assignedTo.length == 1 ? issue.assignedTo[0]?._id : undefined}
                  maxAvatars={issue.assignedTo.length == 1 ? undefined : 5}
                  size="XXS"
                  isCircle
                  enableDetailsModalForGroupedUsers
                  detailsModalHeader={t("Assigned To")}
                />
              </div>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-8">
              <Text className="font-bold">{t("Status")}</Text>
              <IssueStatusPopover
                issue={{
                  id: issueId,
                  // Optional on the row type (see `IssueDetailsRow`) but required by the popover,
                  // which cannot resolve the issue's reviews without it. Erased assertion.
                  spaceId: spaceId!,
                  fileVersionId,
                  statusInReview: issue.statusInReview,
                  reviews: issue.reviews,
                  canEditDefaultStatus: isIssueRequiredPermission(issue, RESOURCE_COMMENT.id),
                }}
                canEdit
                onStatusChanged={refreshIssueAndGrid}
              />
            </div>
            {attachedFiles.length ? (
              <>
                <div className="flex items-center gap-8">
                  <Text className="font-bold">{t("Attachments")}</Text>
                </div>
                <div className="flex flex-col gap-2.5">
                  {attachedFiles.map((file: AttachedFile, index: number) => (
                    <div key={index} className="flex items-center gap-2.5">
                      <ButtonComponent
                        type="button"
                        buttonType={BUTTON_TYPES.TEXT_BUTTON}
                        onClick={() => handleOpenLink(file, false)}
                      >
                        {file.name}
                      </ButtonComponent>
                      <ButtonComponent
                        type="button"
                        buttonType={BUTTON_TYPES.TEXT_BUTTON}
                        onClick={() => handleOpenLink(file, true)}
                      >
                        <BiLinkExternal />
                      </ButtonComponent>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            <div>
              <CustomFields customMetaData={issue.customMetaData} dueDate={issue.dueDate} />
            </div>
          </div>

          {fileVersionId && (
            <div className="pt-7">
              <CommentsList
                issueId={issueId}
                // Branded and nullable on the issue schema; the prop declares a plain `string`.
                offialCommentId={issue.officialCommentId as string | undefined}
                elements={issue.viewpoint ? JSON.parse(issue.viewpoint).selection : {}}
                fileVersionId={fileVersionId}
                showAddComment={false}
              />
            </div>
          )}
          <Accordion
            disableGutters
            expanded={accordionState.access}
            onChange={() => setAccordionState((prev) => ({ ...prev, access: !prev.access }))}
            className="border-0 shadow-none"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0">
              {accessHeader()}
            </AccordionSummary>
            <AccordionDetails>{accessContent()}</AccordionDetails>
          </Accordion>

          {rowData.formTemplate && (
            <Accordion
              disableGutters
              expanded={accordionState.customFields}
              onChange={() => setAccordionState((prev) => ({ ...prev, customFields: !prev.customFields }))}
              className="border-0 shadow-none"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="px-0">
                {customFieldsHeader()}
              </AccordionSummary>
              <AccordionDetails>{customFieldsContent()}</AccordionDetails>
            </Accordion>
          )}
        </div>
      )}
    </div>
  );

  const renderEmptyState = (message: string) => (
    <div className="flex w-160 h-full border border-[#C6D8EC] rounded-lg bg-white items-center justify-center flex-col">
      <IconComponent name="NoData" width="18.75rem" height="18.8125rem" />
      <Text sx={{ textAlign: "center" }}>{message}</Text>
    </div>
  );

  if (sideBarState === "display-row") {
    return renderSideBar();
  }

  if (sideBarState === "no-records") {
    return renderEmptyState(t("No file selected"));
  }

  return renderEmptyState(t("Multiple Files Selected"));
}

export default IssueDetails;
